import { ChangeDetectorRef, Component, ElementRef, OnInit, SecurityContext, ViewChild, inject, signal } from '@angular/core';
import { GoogleMap, MapAdvancedMarker, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { AppState } from 'src/app/app.state';
import { LatLngBounds, LatLng } from 'src/app/shared/models/bounds.model';
import { AppDataService } from 'src/app/shared/services/app-data.service';
import {
  Address,
  GeoZonesRoot,
  Location,
  LocationMapSettings,
  LocationStats,
  MapLocation,
  RestrictedLocation,
  RestrictedLocationQuery,
} from '../ok-app-emulator.model';
import { MatDialog } from '@angular/material/dialog';
import { LocationDetailsInMapComponent } from '../location-details-in-map/location-details-in-map.component';
import { AddressService } from 'src/app/core/services/address.service';
import { Address as CommonAddressModel } from 'src/app/shared/models/address';
import { LocalStorageDataService } from 'src/app/shared/services/local-storage.service';
import moment from 'moment';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, tap, BehaviorSubject, firstValueFrom } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { ChargingSpeed } from 'src/app/shared/enums/charging-speed.enum';
import { ActivatedRoute } from '@angular/router';
import { CommentComponent } from '../comment/comment.component';
import { SnackbarService } from 'ok-angular-shared/components';
import { environment } from 'src/environments/environment';
import { waitUntil } from 'src/app/shared/helpers/waitUntil';
import { LocationStatsComponent } from '../location-stats/location-stats.component';
import { ClearTextDirectiveDirective } from '../../../shared/directives/clear-text-directive.directive';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButtonToggle, MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatInput } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSortModule } from '@angular/material/sort';

@Component({
  selector: 'adm-location-map',
  templateUrl: './location-map.component.html',
  styleUrls: ['./location-map.component.scss'],
  imports: [
    LocationStatsComponent,
    GoogleMap,
    MapAdvancedMarker,
    MapInfoWindow,
    ReactiveFormsModule,
    ClearTextDirectiveDirective,
    MatIcon,
    MatFormField,
    MatTooltip,
    MatButtonToggle,
    MatInput,
    MatPrefix,
    MatSuffix,
    MatCheckbox,
    MatSortModule,
    MatButtonToggleModule,
  ],
})
export class LocationMapComponent implements OnInit {
  appState = inject(AppState);
  private appDataService = inject(AppDataService);
  private dialog = inject(MatDialog);
  private addressService = inject(AddressService);
  private localStorageDataService = inject(LocalStorageDataService);
  private sanitizer = inject(DomSanitizer);
  private activatedRoute = inject(ActivatedRoute);
  private snackbarService = inject(SnackbarService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  @ViewChild(GoogleMap, { static: false }) map!: GoogleMap;
  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;
  @ViewChild('search', { static: true }) searchElement!: ElementRef;
  @ViewChild('activeLocationContent') activeLocationContentRef?: ElementRef<HTMLElement>;
  public options = signal<google.maps.MapOptions | undefined>(undefined);
  public allPublicLocations: Location[] = [];
  public shownLocations = signal<MapLocation[]>([]);
  public shownLocationsUnfiltered: MapLocation[] = [];
  public places: google.maps.places.Place[] = [];
  public filteredLocations: MapLocation[] = [];
  public query: string | null = null;
  public searchResultClicked = false;
  public locationStats!: LocationStats;
  public showLocationStats = true;
  public markers: MapMarker[] = [];
  public activeLocation!: MapLocation;
  public chargingSpeed: typeof ChargingSpeed = ChargingSpeed;
  private defaultLocation = new LatLng(56.276098, 10.668337);
  public chargingSpeedFilters: ChargingSpeed[] = [ChargingSpeed.Normal, ChargingSpeed.Fast, ChargingSpeed.Super];
  public locationMapSettings: BehaviorSubject<LocationMapSettings> = new BehaviorSubject<LocationMapSettings>({
    zoom: 10,
    center: this.defaultLocation,
    mapSearchForm: null,
  });

  private defaultBounds = new LatLngBounds(
    new LatLng(53.20128828027204, 3.058275827267236),
    new LatLng(58.821017370712205, 21.998705514767234)
  );

  placeHolderText = 'Søg efter vejnavn, postnummer eller by';
  public moment = moment;
  public mapReady = signal(false);

  private locationMapKey = 'locationMapSettings';
  private customerNumbers: number[] = [];
  private mokUserNumber: number | undefined;
  private deviceId: string | undefined;
  private deeplinkedMokUser = false;
  public mapSearchForm: FormGroup = new FormGroup({
    mapSearchInput: new FormControl(''),
    mapSearchInputType: new FormControl('address'),
    mapSearchOnlyShowMatchingLocations: new FormControl(false),
  });

  get mapSearchInput() {
    return this.mapSearchForm.controls['mapSearchInput'];
  }
  get mapSearchOnlyShowMatchingLocations() {
    return this.mapSearchForm.controls['mapSearchOnlyShowMatchingLocations'];
  }

  get mapSearchInputType() {
    return this.mapSearchForm.controls['mapSearchInputType'];
  }

  private getChargingSpeedFromConnectorPower(power: number): ChargingSpeed {
    return power > 75 ? ChargingSpeed.Super : power >= 23 ? ChargingSpeed.Fast : ChargingSpeed.Normal;
  }
  async ngOnInit() {
    //due to ExpressionChangedAfterItHasBeenCheckedError
    this.changeDetectorRef.detectChanges();

    const locationMapSettings: LocationMapSettings = this.localStorageDataService.get(this.locationMapKey);
    if (locationMapSettings) {
      this.locationMapSettings.next(locationMapSettings);
    }

    await google.maps.importLibrary('maps');
    await this.populateAllPublicLocations();
    this.initializeMap();
    this.initSearchBoxListener();
    this.evaluateQueryParams(locationMapSettings);
  }
  public locationsFilteredBySpeed() {
    this.shownLocations.set(
      this.shownLocationsUnfiltered.filter(l => {
        return l.locationConnectors?.some(c => {
          const chargingSpeed = this.getChargingSpeedFromConnectorPower(c.power);
          return this.chargingSpeedFilters.includes(chargingSpeed);
        });
      })
    );
  }

  public handleChargingSpeed(type: ChargingSpeed) {
    const addOrRemove = (arr: ChargingSpeed[], item: ChargingSpeed) =>
      arr.includes(item) ? arr.filter((i: ChargingSpeed) => i !== item) : [...arr, item];

    this.chargingSpeedFilters = addOrRemove(this.chargingSpeedFilters, type);
    this.locationsFilteredBySpeed();
  }

  private setPlaceholderText() {
    switch (this.mapSearchInputType.value) {
      case 'mokUserNumber':
        this.placeHolderText = 'Søg efter MOK brugernr.';
        break;
      case 'deviceId':
        this.placeHolderText = 'Søg efter DeviceID.';
        break;
      case 'customerNumber':
        this.placeHolderText = 'Søg efter kundenr.';
        break;
      case 'chargingStationIdentifier':
        this.placeHolderText = 'Søg efter ladestander id';
        break;
      case 'location':
        this.placeHolderText = 'Søg efter offentlig lokation (navn, addresse, postnummer, by, lokationsid)';
        break;
      default:
        this.placeHolderText = 'Søg efter vejnavn, postnummer eller by';
        break;
    }
  }

  private initSearchBoxListener() {
    this.mapSearchForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(searchTerm => this.sanitizer.sanitize(SecurityContext.URL, searchTerm))
      )
      .subscribe(async val => {
        this.getLocations();
        this.shownLocations.update(locations => locations.filter(l => !l.isPlace && !l.isRestricted));
        this.shownLocationsUnfiltered = this.shownLocations();
        this.setPlaceholderText();
        this.query = val.mapSearchInput;
        this.places = [];
        this.filteredLocations = [];
        this.searchResultClicked = false;
        this.showLocationStats = true;
        this.mokUserNumber = undefined;
        this.deviceId = undefined;
        this.customerNumbers = [];
        if (!this.query) {
          if (this.locationMapSettings.getValue().mapSearchForm) {
            this.locationMapSettings.getValue().mapSearchForm!.mapSearchInput = '';
          }
          this.gotoLatLng(this.defaultLocation.lat, this.defaultLocation.long, 7);
          return;
        }

        switch (val.mapSearchInputType) {
          case 'mokUserNumber':
            {
              const mokUserNumber: number = this.parseMokUserNumber(this.query);

              if (mokUserNumber > 0) {
                this.mokUserNumber = mokUserNumber;
                this.getRestrictedLocations({ customerNumbers: [], mokUserNumber: mokUserNumber, deviceId: null });
                if (val.mapSearchOnlyShowMatchingLocations) {
                  this.shownLocations.set([]);
                }
              }
            }
            break;
          case 'deviceId':
            {
              const deviceId = this.query.trim();
              if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(deviceId)) {
                this.snackbarService.info('DeviceID skal være et gyldigt GUID (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx).');
                return;
              }
              if (deviceId) {
                this.deviceId = deviceId;
                this.getRestrictedLocations({ customerNumbers: [], mokUserNumber: 0, deviceId: deviceId });
                if (val.mapSearchOnlyShowMatchingLocations) {
                  this.shownLocations.set([]);
                }
              }
            }
            break;
          case 'customerNumber':
            this.customerNumbers = this.parseCustomerNumbers(this.query);
            if (this.customerNumbers.length > 0) {
              this.getRestrictedLocations({ customerNumbers: this.customerNumbers, mokUserNumber: 0, deviceId: null });
            }
            break;
          case 'location':
            {
              const locations = this.searchLocations(this.query);
              this.filteredLocations = [];

              if (locations.length === 1) {
                this.gotoLatLng(locations[0].latitude, locations[0].longitude);
              } else if (locations.length > 0) {
                this.filteredLocations.push(...locations);
              } else {
                this.snackbarService.info(`Ingen lokation matcher '${this.query}'`);
              }
            }
            break;
          case 'chargingStationIdentifier':
            {
              const location = this.getLocationChargingStationIdentifier(this.query);
              if (location) {
                this.gotoLatLng(location.latitude, location.longitude);
              } else {
                this.snackbarService.info(`Ingen ladestander id matcher '${this.query}'`);
              }
            }
            break;
          default: {
            const request = {
              textQuery: this.query,
              language: 'da-DK',
              region: 'DK',
              locationRestriction: {
                east: this.defaultBounds.northEast.long,
                north: this.defaultBounds.northEast.lat,
                west: this.defaultBounds.southWest.long,
                south: this.defaultBounds.southWest.lat,
              } as google.maps.LatLngBoundsLiteral,
              fields: ['displayName', 'addressComponents', 'adrFormatAddress', 'formattedAddress', 'location', 'businessStatus'],
              maxResultCount: 20,
            };
            const { Place } = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary;
            const { places } = await Place.searchByText(request);
            this.places = [];

            if (places.length === 1) {
              this.gotoPlace(places[0]);
            } else if (places.length > 0) {
              this.places.push(...places);
            }
            break;
          }
        }
        this.locationMapSettings.next({
          zoom: this.locationMapSettings.getValue().zoom,
          center: this.locationMapSettings.getValue().center,
          mapSearchForm: val,
        });
        this.localStorageDataService.set(this.locationMapKey, this.locationMapSettings.getValue());
      });
  }

  public gotoPlace(place: google.maps.places.Place) {
    if (!place.location) return;
    this.searchResultClicked = true;
    this.gotoLatLng(place.location.lat(), place.location.lng());

    this.shownLocations.update(locations => [
      ...locations.filter(l => !l.isPlace),
      {
        latitude: place.location!.lat(),
        longitude: place.location!.lng(),
        name: place.displayName,
        address: place.formattedAddress,
        content: this.getPlaceIcon(),
        isPlace: true,
      } as MapLocation,
    ]);
  }

  public gotoLocation(location: MapLocation) {
    this.searchResultClicked = true;
    this.gotoLatLng(location.latitude, location.longitude);
  }
  public async gotoLatLng(lat: number, lng: number, zoom = 13) {
    // Wait for the map to be ready, before panning
    await waitUntil(() => !!(this.mapReady() && this.map && google.maps.LatLng));

    this.map.panTo({ lat: lat, lng: lng });
    this.locationMapSettings.next({
      zoom: zoom,
      center: new LatLng(lat, lng),
      mapSearchForm: this.locationMapSettings.getValue().mapSearchForm,
    });
    this.localStorageDataService.set(this.locationMapKey, this.locationMapSettings.getValue());
  }

  private evaluateQueryParams(locationMapSettings: LocationMapSettings) {
    this.activatedRoute.queryParamMap.subscribe(async queryParamMap => {
      if (queryParamMap.get('chargingStationLocationIdentifier')) {
        const chargingStationLocationIdentifier = queryParamMap.get('chargingStationLocationIdentifier');
        await this.getLocations();
        const location = this.allPublicLocations.find(l => l.locationId === chargingStationLocationIdentifier);
        if (location) {
          this.mapSearchForm.setValue({
            mapSearchInput: location.name ?? '',
            mapSearchInputType: 'location',
            mapSearchOnlyShowMatchingLocations: false,
          });
          // Wait for the map to be ready, before panning
          await waitUntil(() => !!(this.mapReady() && this.map && google.maps.LatLng));

          this.gotoLatLng(location.latitude, location.longitude, 12);
        }
      } else if (queryParamMap.get('query')) {
        let mapSearchOnlyShowMatchingLocations = false;
        this.deeplinkedMokUser =
          queryParamMap.get('mapSearchInputType') === 'mokUserNumber' || queryParamMap.get('mapSearchInputType') === 'deviceId';
        if (this.deeplinkedMokUser) {
          mapSearchOnlyShowMatchingLocations = true;
        }

        this.mapSearchForm.setValue({
          mapSearchInput: queryParamMap.get('query') || '',
          mapSearchInputType: queryParamMap.get('mapSearchInputType') || 'address',
          mapSearchOnlyShowMatchingLocations: mapSearchOnlyShowMatchingLocations,
        });

        let zoom = this.locationMapSettings.getValue().zoom;
        if (queryParamMap.get('mapSearchInputType') === 'mokUserNumber') {
          zoom = 8;
        }

        this.locationMapSettings.next({
          zoom: zoom,
          center: this.locationMapSettings.getValue().center,
          mapSearchForm: this.mapSearchForm.value,
        });
        this.localStorageDataService.set(this.locationMapKey, this.locationMapSettings.getValue());
      } else {
        if (locationMapSettings?.mapSearchForm) {
          this.mapSearchInput.setValue(locationMapSettings?.mapSearchForm.mapSearchInput);
          this.mapSearchInputType.setValue(locationMapSettings?.mapSearchForm.mapSearchInputType);
        } else {
          // No saved search and no query params - load the markers right away instead of waiting for user interaction
          this.getLocations();
        }
      }
    });
  }

  private populateLocationStats(locations: MapLocation[] = []): void {
    const locationConnectors = locations?.flatMap(l => l.locationConnectors).filter(l => l !== undefined);
    const restrictedLocationSpots = locations?.flatMap(l => l.restrictedLocationSpots).filter(l => l !== undefined);
    if (locationConnectors.length > 0) {
      this.locationStats = {
        locationCount: locations?.length,
        chargingStationCount: locations?.reduce((sum, current) => sum + current.chargingStationCount, 0),
        connectorCount: locationConnectors.length,
        slowConnectorCount: locationConnectors.filter(c => c.power < 23).length,
        fastConnectorCount: locationConnectors.filter(c => c.power >= 23 && c.power <= 75).length,
        rapidConnectorCount: locationConnectors.filter(c => c.power > 75).length,
      } as LocationStats;
    } else if (restrictedLocationSpots.length > 0) {
      this.locationStats = {
        locationCount: locations?.length,
        chargingStationCount: -1,
        connectorCount: restrictedLocationSpots.length,
        slowConnectorCount: restrictedLocationSpots.filter(
          c => c.connectorTypes.flatMap(ct => ct.toString().toLocaleLowerCase()).indexOf('type2') > -1
        ).length,
        fastConnectorCount: restrictedLocationSpots.filter(
          c => c.connectorTypes.flatMap(ct => ct.toString().toLocaleLowerCase()).indexOf('chademo') > -1
        ).length,
        rapidConnectorCount: restrictedLocationSpots.filter(
          c => c.connectorTypes.flatMap(ct => ct.toString().toLocaleLowerCase()).indexOf('ccs') > -1
        ).length,
      } as LocationStats;
    }
  }

  private parseMokUserNumber(mokUserNumberInput: string): number {
    if (!mokUserNumberInput || mokUserNumberInput.length === 0) {
      return 0;
    }

    const parsedMokUserNumber = Number(mokUserNumberInput);
    if (isNaN(parsedMokUserNumber)) {
      return 0;
    }
    return parsedMokUserNumber;
  }

  private parseCustomerNumbers(customerNumbersInput: string): number[] {
    if (!customerNumbersInput || customerNumbersInput.length === 0) {
      return [];
    }
    const customerNumbersInputArray = customerNumbersInput.split(',');
    let customerNumbers: number[] = [];
    customerNumbersInputArray.forEach(customerNumber => {
      const numberCheck = Number(customerNumber);
      if (!isNaN(numberCheck) && customerNumbers.indexOf(numberCheck) === -1 && numberCheck > 0) {
        customerNumbers.push(numberCheck);
      }
    });
    customerNumbers = customerNumbers.sort(function (a: number, b: number) {
      return a - b;
    });
    return customerNumbers;
  }

  private getLocationChargingStationIdentifier(chargingStationIdentifierInput: string): Location | null {
    if (!chargingStationIdentifierInput || chargingStationIdentifierInput.length === 0) {
      return null;
    }

    chargingStationIdentifierInput = chargingStationIdentifierInput.toLocaleLowerCase().trim();
    const location = this.allPublicLocations.filter(
      l => l.chargingStations.findIndex(c => c.chargingStationId.toLocaleLowerCase() === chargingStationIdentifierInput) > -1
    )[0];
    if (location) {
      return location;
    }
    return null;
  }
  private searchLocations(locationQuery: string): MapLocation[] {
    if (!locationQuery || locationQuery.length === 0) {
      return [];
    }
    locationQuery = locationQuery.toLocaleLowerCase().trim();
    const locations = this.allPublicLocations.filter(
      l =>
        l.address.road?.toLocaleLowerCase().includes(locationQuery) ||
        l.address.postalCode?.toLocaleLowerCase().includes(locationQuery) ||
        l.address.city?.toLocaleLowerCase().includes(locationQuery) ||
        l.name?.toLocaleLowerCase().includes(locationQuery) ||
        l.locationId.toLocaleLowerCase().includes(locationQuery) ||
        l.chargingStations.findIndex(c => c.chargingStationId.toLocaleLowerCase() === locationQuery) > -1
    );

    const filteredLocations: MapLocation[] = [];
    locations.forEach(location => {
      filteredLocations.push(this.mapToMapLocation(location));
    });

    return filteredLocations;
  }

  private initializeMap() {
    const zoom = this.locationMapSettings.getValue().zoom;
    const center = this.locationMapSettings.getValue().center;

    this.options.set({
      mapId: environment.googleMapId,
      mapTypeId: 'roadmap',
      zoomControl: true,
      scrollwheel: true,
      disableDoubleClickZoom: true,
      disableDefaultUI: true,
      tilt: 0,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: google.maps.ControlPosition.BOTTOM_CENTER,
        mapTypeIds: ['roadmap', 'satellite'],
      },
      fullscreenControl: true,
      center: { lat: center.lat, lng: center.long },
      zoom: zoom,
    });

    this.mapReady.set(true);
  }
  private async getLocations() {
    this.markers = [];
    if (this.allPublicLocations.length === 0) {
      await this.populateAllPublicLocations();
    }

    const shownLocations: MapLocation[] = [];
    this.allPublicLocations.forEach((location: Location) => {
      shownLocations.push(this.mapToMapLocation(location));
    });
    this.shownLocations.set(shownLocations);
    this.shownLocationsUnfiltered = shownLocations;

    this.populateLocationStats(shownLocations);
  }
  private async populateAllPublicLocations() {
    const geoZoneRoot: GeoZonesRoot = await firstValueFrom(this.appDataService.getPublicLocations());
    this.allPublicLocations = geoZoneRoot.geoZones.flatMap(g => g.locations);
  }

  private getRestrictedLocations(restrictedLocationQuery: RestrictedLocationQuery): void {
    this.appDataService
      .getRestrictedLocations(
        this.defaultBounds,
        restrictedLocationQuery.customerNumbers,
        restrictedLocationQuery.mokUserNumber,
        restrictedLocationQuery.deviceId
      )
      .subscribe({
        next: (restrictedLocations: RestrictedLocation[]) => {
          this.showLocationStats = !this.mapSearchOnlyShowMatchingLocations.value;
          const shownLocations = this.mapSearchOnlyShowMatchingLocations.value ? [] : [...this.shownLocations()];
          restrictedLocations.forEach((location: RestrictedLocation) => {
            shownLocations.push(this.mapRestrictedLocationToMapLocation(location));
          });
          this.shownLocations.set(shownLocations);
          this.shownLocationsUnfiltered = shownLocations;
          if (restrictedLocations.length === 0) {
            this.snackbarService.info('Ingen begrænsede lokationer fundet, der matcher søgningen');
          } else if (restrictedLocations.length === 1 && !this.deeplinkedMokUser) {
            const firstRestrictedLocation = restrictedLocations[0];
            this.gotoLatLng(firstRestrictedLocation.latitude, firstRestrictedLocation.longitude, 11);
          }
          this.populateLocationStats(shownLocations);
        },
      });
  }

  boundsChanged(isInit: boolean) {
    let center = new LatLng(this.map.getBounds()!.getCenter().lat(), this.map.getBounds()!.getCenter().lng());
    if (isInit) {
      center = this.locationMapSettings.getValue().center;
    }
    this.locationMapSettings.next({
      zoom: this.locationMapSettings.getValue().zoom,
      center: center,
      mapSearchForm: this.locationMapSettings.getValue().mapSearchForm,
    });
    this.localStorageDataService.set(this.locationMapKey, this.locationMapSettings.getValue());
  }

  onForceLocationUpdate() {
    this.allPublicLocations = [];
    this.getLocations();
  }

  closeInfo() {
    if (this.activeLocation) {
      this.activeLocation.content = this.getIcon(this.activeLocation.chargingSpeed, this.activeLocation.isRestricted);
      this.infoWindow.close();
    }
  }

  openInfo(location: MapLocation, marker: MapAdvancedMarker) {
    if (location.isPlace) {
      return;
    }

    if (this.activeLocation) {
      this.activeLocation.content = this.getIcon(this.activeLocation.chargingSpeed, this.activeLocation.isRestricted);
    }
    location.content = this.getIcon(location.chargingSpeed, location.isRestricted, 36);
    this.activeLocation = location;
    this.infoWindow.open(marker);

    // the map otherwise swallows clicks on the info window content (e.g. the anchor tag) via its own gesture handling
    setTimeout(() => {
      if (this.activeLocationContentRef) {
        google.maps.OverlayView.preventMapHitsAndGesturesFrom(this.activeLocationContentRef.nativeElement);
      }
    });
  }

  private formatAddressToString(address: Address): string {
    const commonAddressModel = new CommonAddressModel(
      address.city,
      address.postalCode,
      address.road,
      address.number,
      address.letter,
      undefined,
      undefined,
      undefined
    );
    return this.addressService.formatAddressToString(commonAddressModel);
  }

  public openLocationDetails(location: MapLocation) {
    // Blur first so CDK isn't blocked from setting aria-hidden on the background while the info window still holds focus
    (document.activeElement as HTMLElement | null)?.blur();

    this.dialog.open(LocationDetailsInMapComponent, {
      data: {
        location: location,
        customerNumbers: this.customerNumbers,
        mokUserNumber: this.mokUserNumber,
        deviceId: this.deviceId,
        version: 'v4',
      },
      disableClose: false,
      width: '512px',
      panelClass: 'location-detail',
    });
  }

  public openComment(location: MapLocation) {
    this.dialog.open(CommentComponent, {
      data: { comment: location.visibilityComment },
      disableClose: false,
      width: '368px',
      panelClass: 'location-comment',
    });
  }

  public getIcon(chargingSpeed = ChargingSpeed.Normal, isLimited = false, scaledSize: number | null = null) {
    scaledSize = scaledSize !== null ? scaledSize : 27;
    const icon = document.createElement('img');
    switch (chargingSpeed) {
      case ChargingSpeed.Super:
        icon.src = isLimited ? '/assets/icons/charging_orange_burnt_limited-access.svg' : '/assets/icons/charging_orange_burnt.svg';
        break;
      case ChargingSpeed.Fast:
        icon.src = isLimited ? '/assets/icons/charging_orange_limited-access.svg' : '/assets/icons/charging_orange.svg';
        break;
      default:
        icon.src = isLimited ? '/assets/icons/charging_green_limited-access.svg' : '/assets/icons/charging_green.svg';
        break;
    }
    icon.width = scaledSize;
    icon.height = scaledSize;

    return icon;
  }

  private getPlaceIcon() {
    const icon = document.createElement('img');
    icon.src = '/assets/icons/map-pin.svg';
    icon.width = 50;
    icon.height = 50;
    return icon;
  }

  private mapToMapLocation(location: Location): MapLocation {
    const chargingSpeed = this.getChargingSpeed(location.maxChargingPowerOnLocation);
    return {
      address: this.formatAddressToString(location.address),
      chargingSpeed: chargingSpeed,
      latitude: location.latitude,
      longitude: location.longitude,
      locationId: location.locationId,
      name: location.name,
      chargingStationCount: location.chargingStations.length,
      connectorCount: location.chargingStations.flatMap(c => c.connectors).length,
      locationConnectors: location.chargingStations.flatMap(c => c.connectors),
      visibilityComment: location.visibilityComment,
      content: this.getIcon(chargingSpeed),
      isPlace: false,
    } as MapLocation;
  }

  private mapRestrictedLocationToMapLocation(location: RestrictedLocation): MapLocation {
    return {
      address: location.address.road + ' ' + location.address.number,
      chargingSpeed: location.chargingSpeed,
      latitude: location.latitude,
      longitude: location.longitude,
      locationId: location.locationId,
      name: location.name,
      chargingStationCount: -1,
      connectorCount: location.spots.length,
      restrictedLocationSpots: location.spots,
      visibilityComment: location.visibilityComment,
      content: this.getIcon(location.chargingSpeed, true),
      isPlace: false,
      isRestricted: true,
    } as MapLocation;
  }

  private getChargingSpeed(maxChargingPowerOnLocation: number): ChargingSpeed {
    return maxChargingPowerOnLocation > 75
      ? ChargingSpeed.Super
      : maxChargingPowerOnLocation >= 23
        ? ChargingSpeed.Fast
        : ChargingSpeed.Normal;
  }
}

export interface MapSearchForm {
  mapSearchInput: string;
  mapSearchInputType: string;
}
